import React from 'react';
import type { MapFeature } from '../types';

// ─────────────────────────────────────────────────────────────
// PaddockDisambiguateMenu
// Shown when the user clicks a point where multiple paddocks
// overlap. Lists candidates with name (left) and area (right).
// Hovering highlights the corresponding polygon on the map.
// ─────────────────────────────────────────────────────────────

interface Props {
  /** Click position relative to the map container. */
  x: number;
  y: number;
  paddocks: MapFeature[];
  /** Called while hovering a row — null when leaving all rows. */
  onHover: (paddock: MapFeature | null) => void;
  onSelect: (paddock: MapFeature) => void;
  /** Called when the user clicks the backdrop (dismisses without selection). */
  onClose: () => void;
}

const PaddockDisambiguateMenu = ({
  x,
  y,
  paddocks,
  onHover,
  onSelect,
  onClose,
}: Props) => {
  return (
    <>
      {/* Backdrop — catches outside clicks without interfering with z-stacking */}
      <div
        className="absolute inset-0 z-[20]"
        onClick={onClose}
      />

      {/* Dropdown panel — dynamic left/top require inline style (overlay positioning) */}
      <div
        className="absolute z-[21] overflow-hidden rounded-[var(--radius-button)] bg-white min-w-[13rem] map-card-shadow"
        style={{ left: x, top: y }}
      >
        {/* Header hint */}
        <div className="border-b border-secondary-light px-3 py-1.5 text-[0.6875rem] font-semibold text-secondary-dark">
          Select paddock
        </div>

        {paddocks.map((paddock) => (
          <div
            key={paddock.id}
            className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2 hover:bg-gray-bright"
            onMouseEnter={() => onHover(paddock)}
            onMouseLeave={() => onHover(null)}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(paddock);
            }}
          >
            <span className="truncate text-sm font-medium text-success-dark">
              {paddock.name}
            </span>
            <span className="shrink-0 text-xs tabular-nums text-secondary-dark">
              {paddock.area} ha
            </span>
          </div>
        ))}
      </div>
    </>
  );
};

export default PaddockDisambiguateMenu;
